import { ReactNode } from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { common, financedEmissions } from "../../i18n";
import { FeDashboardIcon, FeGroupIcon, FeReportIcon } from "./FeIcons";

export interface FePageTab {
  name: string;
  pathname: string;
  icon: ReactNode;
}

/** Default financed-emissions tabs (Figma node 4173:54). */
export const FE_PAGE_TABS: FePageTab[] = [
  {
    pathname: "/financed-emissions/dashboard",
    name: "Dashboard",
    icon: <FeDashboardIcon />,
  },
  {
    pathname: "/financed-emissions/borrowers",
    name: "Borrowers",
    icon: <FeGroupIcon />,
  },
  {
    pathname: "/financed-emissions/report",
    name: "Report",
    icon: <FeReportIcon />,
  },
];

/** Equal-width page tabs — Figma "Tab" (node 4173:54): white 8px-radius bar,
 *  44px tabs with 24px icon + 12px gap + 16px medium label; active tab is
 *  green with white text, inactive is white with near-black text. */
const FePageTabs = ({ tabs = FE_PAGE_TABS }: { tabs?: FePageTab[] }) => {
  const { pathname } = useLocation();
  const { t } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });

  return (
    <Box
      display="flex"
      bgcolor="var(--white)"
      borderRadius="8px"
      overflow="hidden"
    >
      {tabs.map((tab) => {
        // Highlight on the exact route and any nested route (e.g. a borrower
        // detail under /borrowers/:id keeps the Borrowers tab active).
        const active =
          pathname === tab.pathname ||
          pathname.startsWith(`${tab.pathname}/`);
        return (
          <Link
            key={tab.pathname}
            to={tab.pathname}
            style={{ flex: 1, display: "flex", textDecoration: "none" }}
          >
            <ButtonBase
              sx={{
                flex: 1,
                height: 44,
                gap: "12px",
                borderRadius: "8px",
                color: active ? "var(--white)" : "var(--button-text-enable5)",
                backgroundColor: active ? "var(--green)" : "var(--white)",
                "& svg": { fontSize: 24 },
              }}
            >
              {tab.icon}
              <Typography fontSize="16px" fontWeight={500} lineHeight="24px">
                {t(tab.name)}
              </Typography>
            </ButtonBase>
          </Link>
        );
      })}
    </Box>
  );
};

export default FePageTabs;
